import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { EventActions } from './event.actions';
import { Event } from './model/event.model';
import { Participant } from '../user-management/model/participant.model';

export const eventFeatureKey = 'event';

export const eventAdapter = createEntityAdapter<Event>();

export interface State extends EntityState<Event> {
    selectedEvent: Event | undefined;
}

export const initialState: State = eventAdapter.getInitialState({
    selectedEvent: undefined
});

export const getSelectedEvent = (state: State) => state.selectedEvent;

const eventReducer = createReducer(
    initialState,
    on(EventActions.loadEventsSuccess, (state, { events }) => eventAdapter.setAll(events, state)),
    on(EventActions.saveEventSuccess, (state, { events }) => eventAdapter.upsertMany(events, state)),
    on(EventActions.loadEventByIdSuccess, (state, { event }) => {
        return eventAdapter.upsertOne(event, { ...state, selectedEvent: event });
    }),
    on(EventActions.addParticipant, (state, { participant }) => {
        return {
            ...state,
            selectedEvent: {
                ...state.selectedEvent!,
                participants: state.selectedEvent!.participants.concat(participant)
            }
        };
    }),
    on(EventActions.removeParticipant, (state, { index }) => {
        return {
            ...state,
            selectedEvent: {
                ...state.selectedEvent!,
                participants: [...state.selectedEvent!.participants.slice(0, index), ...state.selectedEvent!.participants.slice(index + 1)]
            }
        };
    }),
    on(EventActions.addWaitlistUser, (state, { waitlistUser }) => {
        return {
            ...state,
            selectedEvent: {
                ...state.selectedEvent!,
                waitlistUsers: state.selectedEvent!.waitlistUsers.concat(waitlistUser)
            }
        };
    }),
    on(EventActions.removeWaitlistUser, (state, { index }) => {
        return {
            ...state,
            selectedEvent: {
                ...state.selectedEvent!,
                waitlistUsers: [...state.selectedEvent!.waitlistUsers.slice(0, index), ...state.selectedEvent!.waitlistUsers.slice(index + 1)]
            }
        };
    }),
    on(EventActions.cancelEventSuccess, (state, { event }) => eventAdapter.updateOne({ id: event.id!, changes: { takePlace: event.takePlace } }, state)),
    on(EventActions.eventTakePlaceSuccess, (state, { event }) => eventAdapter.updateOne({ id: event.id!, changes: { takePlace: event.takePlace } }, state)),
    on(EventActions.moveEventSuccess, (state, { event }) => eventAdapter.updateOne({ id: event.id!, changes: event }, state)),
    on(EventActions.deleteEventSuccess, (state, { event, deleteSeries }) => {
        // when deleting series find all entities with the desired seriesId
        if (!deleteSeries) {
            return eventAdapter.removeOne(event.id!, state);
        } else {
            // find all entities with the same deleteSeriesId
            const ids: string[] = [];
            Object.entries(state.entities).forEach(([key, value]) => {
                if (value?.seriesId === event.seriesId) {
                    ids.push(key);
                }
            });
            return eventAdapter.removeMany(ids, state);
        }
    }),
    on(EventActions.updateSelectedEvent, (state, { values }) => {
        return {
            ...state,
            selectedEvent: {
                ...state.selectedEvent!,
                start: values.start,
                end: values.end,
                description: values.description,
                instructor: values.instructor,
                room: values.room
            }
        };
    }),
    on(EventActions.resetSelectedEvent, (state) => {
        return { ...state, selectedEvent: undefined };
    }),
    on(EventActions.setUserComment, (state, { index, comment }) => {
        const newParticipants: Participant[] = state.selectedEvent!.participants.map((participant, idx) => {
            if (index === idx) {
                return { ...participant, userAccount: { ...participant.userAccount, comment: comment } };
            }
            return participant;
        });
        return { ...state, selectedEvent: { ...state.selectedEvent!, participants: newParticipants } };
    }),
    on(EventActions.subscribeToEventSuccess, (state, { event }) => eventAdapter.upsertOne(event, state)),
    on(EventActions.unsubscribeFromEventSuccess, (state, { event }) => eventAdapter.upsertOne(event, state)),
    on(EventActions.subscribeToWorkshopSuccess, (state, { events }) => eventAdapter.upsertMany(events, state)),
    on(EventActions.unsubscribeFromAllWorkshopEventsSuccess, (state, { events }) => eventAdapter.upsertMany(events, state))
);

export function reducer(state: State | undefined, action: Action) {
    return eventReducer(state, action);
}
